// exclusionFlow — 「外した版を焼く」job の開始 → ポーリング → 結果
// (荒らし復旧 第 2 段階 ②、vandalism-recovery-ui-2026-09-27.md §3)。
//
// サーバは `POST .../exclusion` で 202 `{jobId}` を返し、画面が `GET .../:jobId` を
// 一定間隔で叩く (焼きは数十秒かかり得るので同期 HTTP にしない)。この関数は
//   - 開始の失敗 (busy / not-available ...) を短い日本語にする
//   - phase が変わるたびに `onPhase` を呼ぶ
//   - 通信の一時失敗は数回まで許容 (job はサーバ側で走り続けている)
//   - job 404 (dispatcher 再起動で消えた) を専用文言にする
// を担う。DOM に触れない (sleep / fetch は注入可能)。

import {
  type ExclusionBakeStatsView,
  type ExclusionJobPhase,
  type FetchExclusionJobOutcome,
  fetchExclusionJob,
  type StartExclusionOutcome,
  startExclusion,
} from '../infrastructure/pastCanvas/exclusionClient.js';
import {
  EXCLUSION_JOB_LOST_MESSAGE,
  EXCLUSION_POLL_ERROR_MESSAGE,
  EXCLUSION_START_ERROR_MESSAGES,
  exclusionFailureMessage,
} from './exclusionCandidates.js';

export type ExclusionBakeOutcome =
  | {
      readonly outcome: 'done';
      readonly pastCanvasId: string;
      readonly snapshotSeq: number;
      readonly stats: ExclusionBakeStatsView;
    }
  | { readonly outcome: 'failed'; readonly message: string; readonly code?: string };

export interface RunExclusionBakeInput {
  readonly dispatcherUrl: string;
  readonly roomId: string;
  readonly adminToken?: string;
  readonly pastCanvasId: string | null;
  readonly excludeUids: readonly string[];
  readonly excludeSids: readonly string[];
  readonly onPhase?: (phase: ExclusionJobPhase) => void;
  readonly signal?: AbortSignal;
}

export interface RunExclusionBakeDeps {
  readonly start?: typeof startExclusion;
  readonly poll?: typeof fetchExclusionJob;
  readonly sleep?: (ms: number) => Promise<void>;
  readonly pollIntervalMs?: number;
  /** 連続で状態確認に失敗した回数の上限 (超えたら諦める)。 */
  readonly maxConsecutivePollErrors?: number;
  /** 全体の上限 (ms)。サーバの bake は数十秒〜数分。 */
  readonly maxWaitMs?: number;
  readonly now?: () => number;
}

const realSleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

export const runExclusionBake = async (
  input: RunExclusionBakeInput,
  deps: RunExclusionBakeDeps = {},
): Promise<ExclusionBakeOutcome> => {
  const start = deps.start ?? startExclusion;
  const poll = deps.poll ?? fetchExclusionJob;
  const sleep = deps.sleep ?? realSleep;
  const interval = deps.pollIntervalMs ?? 1500;
  const maxErrors = deps.maxConsecutivePollErrors ?? 4;
  const maxWait = deps.maxWaitMs ?? 10 * 60_000;
  const now = deps.now ?? Date.now;

  const common = {
    dispatcherUrl: input.dispatcherUrl,
    roomId: input.roomId,
    ...(input.adminToken !== undefined ? { adminToken: input.adminToken } : {}),
    ...(input.signal !== undefined ? { signal: input.signal } : {}),
  };

  const started: StartExclusionOutcome = await start({
    ...common,
    pastCanvasId: input.pastCanvasId,
    excludeUids: input.excludeUids,
    excludeSids: input.excludeSids,
  });
  if (started.outcome !== 'ok') {
    return { outcome: 'failed', message: EXCLUSION_START_ERROR_MESSAGES[started.outcome] };
  }

  const t0 = now();
  let errors = 0;
  let lastPhase: string | null = null;
  for (;;) {
    if (input.signal?.aborted === true) {
      return { outcome: 'failed', message: '中断しました' };
    }
    const res: FetchExclusionJobOutcome = await poll({ ...common, jobId: started.jobId });
    if (res.outcome === 'ok') {
      errors = 0;
      const job = res.job;
      if (job.status === 'done') {
        return {
          outcome: 'done',
          pastCanvasId: job.pastCanvasId,
          snapshotSeq: job.snapshotSeq,
          stats: job.stats,
        };
      }
      if (job.status === 'failed') {
        return {
          outcome: 'failed',
          code: job.error,
          message: exclusionFailureMessage(job.error),
        };
      }
      if (job.phase !== lastPhase) {
        lastPhase = job.phase;
        input.onPhase?.(job.phase);
      }
    } else if (res.outcome === 'not-found') {
      return { outcome: 'failed', message: EXCLUSION_JOB_LOST_MESSAGE };
    } else if (res.outcome === 'unauthorized') {
      return { outcome: 'failed', message: EXCLUSION_START_ERROR_MESSAGES.unauthorized };
    } else {
      errors += 1;
      if (errors >= maxErrors) {
        return { outcome: 'failed', message: EXCLUSION_POLL_ERROR_MESSAGE };
      }
    }
    if (now() - t0 > maxWait) {
      return {
        outcome: 'failed',
        message: '時間がかかっています。過去キャンバスの一覧に結果が出ていないか確認してください',
      };
    }
    await sleep(interval);
  }
};
